import { computed, inject, Injectable, linkedSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { signalStore, withState, withMethods, withComputed } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, fromEvent, map, of, pipe, startWith, switchMap, tap } from 'rxjs';
import { ELoadingEntity } from 'src/models/generic-store.model';
import { withDevtools, withStorageSync, updateState } from '@angular-architects/ngrx-toolkit';
import { baseState, ECurrencyCode, EInstrumentCategory, ELanguage, EManagementType } from 'src/models/common.model';
import { EAppearanceMode, EContractsComposition, IApiClientAccountOut, IFrameworkContractOut, IInvestmentProfile, ILoggedUser, IUserState } from 'src/models/user.model';
import { UserRestApiService } from './user-rest-api.service';
import { withError, withLoading, withoutError, withoutLoading, withRequestStatus } from '../store-features/with-request-status.store-feature';
import { NotificationService } from 'src/services/notification.service';
import { TranslateService } from '@ngx-translate/core';

enum EUserActions {
  CURRENT_USER_REQUEST = '[User] Current User Request',
  CURRENT_USER_RESPONSE = '[User] Current User Response',
  CURRENT_USER_FAILURE = '[User] Current User Failure',

  INVESTMENT_PROFILE_REQUEST = '[User] Investment Profile Request',
  INVESTMENT_PROFILE_RESPONSE = '[User] Investment Profile Response',
  INVESTMENT_PROFILE_FAILURE = '[User] Investment Profile Failure',

  RESET_USER = '[User] Reset user',
  RESET_STATE = '[User] Reset state',
  SET_IS_IDLE = '[User] Set is idle',
  SET_LAST_ACTIVE = '[User] Set last active',
}

const initialUserState: IUserState = {
  loggedUser: null,
  isIdle: false,
  lastActive: null,
  contractsStats: null,
  investmentProfile: null,
};

const STORE_NAME = 'user';

@Injectable({ providedIn: 'root' })
export class UserStore extends signalStore(
  withState(initialUserState),
  withStorageSync(STORE_NAME + 'State'),
  withDevtools(STORE_NAME),
  withRequestStatus(),
  withComputed(store => {
    const darkModeMedia = window.matchMedia('(prefers-color-scheme: dark)');
    const systemPrefersDark = toSignal(
      fromEvent<MediaQueryListEvent>(darkModeMedia, 'change').pipe(
        map(e => e.matches),
        startWith(darkModeMedia.matches),
      ),
      { initialValue: darkModeMedia.matches }
    );

    const appearanceModeSignal = computed<EAppearanceMode | null>(() => store.loggedUser()?.appearanceMode ?? null);
    const appearanceModeHistory = linkedSignal({
      source: appearanceModeSignal,
      computation: (current: EAppearanceMode | null, previous?: { value: EAppearanceMode | null }) => current ?? previous?.value ?? null
    });

    return {
      userCurrencyCode: computed<ECurrencyCode>(() => store.loggedUser()?.currencyCode ?? ECurrencyCode.CZK),
      firstNameVocative: computed<string>(() => store.loggedUser()?.firstNameVocative ?? ''),
      maxRiskScore: computed<number | null>(() => store.loggedUser()?.maxRiskScore ?? null),
      userInstrumentCategories: computed<EInstrumentCategory[]>(() => store.loggedUser()?.instrumentCategories ?? []),
      userManagementType: computed<EManagementType | null>(() => store.loggedUser()?.managementType ?? null),
      appearanceMode: appearanceModeHistory,
      isDarkMode: computed<boolean>(() => {
        // When logging out, loggedUser (and their appearanceMode) is nullified, therefore theme flashes from dark → light, preventing this with getting most recent appearance mode
        const mostRecentAppearance = appearanceModeHistory();
        return (
          mostRecentAppearance === EAppearanceMode.DARK ||
          (mostRecentAppearance === EAppearanceMode.SYSTEM && systemPrefersDark())
          );
      }),
    };
  }),
  withMethods((store, userRestApiService = inject(UserRestApiService), notificationService = inject(NotificationService), translate = inject(TranslateService)) => {

    const getCurrentUser = rxMethod<void>(
      pipe(
        tap(() =>
          updateState(store, EUserActions.CURRENT_USER_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.CURRENT_USER, loadingProps),
          }))
        ),
        switchMap(() =>
          userRestApiService.getUserDetail().pipe(
            map((response) =>
              updateState(store, EUserActions.CURRENT_USER_RESPONSE, ({ loadingProps, errorProps }) => ({
                loggedUser: getValidatedMappedUserResponse(response),
                contractsStats: {
                  contracts: response.validContracts,
                  contractsComposition: getContractsComposition(response.validContracts),
                },
                ...withoutLoading(ELoadingEntity.CURRENT_USER, loadingProps),
                ...withoutError(ELoadingEntity.CURRENT_USER, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EUserActions.CURRENT_USER_FAILURE, ({ loadingProps, errorProps }) => ({
                  loggedUser: null,
                  ...withoutLoading(ELoadingEntity.CURRENT_USER, loadingProps),
                  ...withError(ELoadingEntity.CURRENT_USER, { error, errorParams: null }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const getInvestmentProfile = rxMethod<{ clientId: number }>(
      pipe(
        tap(() =>
          updateState(store, EUserActions.INVESTMENT_PROFILE_REQUEST, ({ loadingProps }) => ({
            ...withLoading(ELoadingEntity.INVESTMENT_PROFILE, loadingProps),
          }))
        ),
        switchMap(({ clientId }) =>
          userRestApiService.getInvestmentProfile(clientId).pipe(
            map((response) =>
              updateState(store, EUserActions.INVESTMENT_PROFILE_RESPONSE, ({ loadingProps, errorProps }) => ({
                investmentProfile: response as IInvestmentProfile,
                ...withoutLoading(ELoadingEntity.INVESTMENT_PROFILE, loadingProps),
                ...withoutError(ELoadingEntity.INVESTMENT_PROFILE, errorProps),
              }))
            ),
            catchError((error) =>
              of(
                updateState(store, EUserActions.INVESTMENT_PROFILE_FAILURE, ({ loadingProps, errorProps }) => ({
                  investmentProfile: null,
                  ...withoutLoading(ELoadingEntity.INVESTMENT_PROFILE, loadingProps),
                  ...withError(ELoadingEntity.INVESTMENT_PROFILE, { error, errorParams: null }, errorProps),
                }))
              )
            )
          )
        )
      )
    );

    const resetLoggedUser = () => {
      updateState(store, EUserActions.RESET_USER, { loggedUser: null });
    }

    const resetState = () => {
      updateState(store, EUserActions.RESET_STATE, { ...initialUserState, ...baseState });
    }

    const setIsIdle = (isIdle: boolean) => {
      updateState(store, EUserActions.SET_IS_IDLE, { isIdle });
    }

    const setLastActive = (lastActive: string | null) => {
      updateState(store, EUserActions.SET_LAST_ACTIVE, { lastActive });
    }

    // Helper methods
    const validateApiUserResponse = (data: unknown): data is IApiClientAccountOut => {
      const availableCurrencies: ECurrencyCode[] = [
        ECurrencyCode.EUR,
        ECurrencyCode.CZK,
      ];

      if (
        typeof data === 'object' &&
        data !== null &&
        'clientAccountId' in data && typeof data.clientAccountId === 'number' &&
        'currencyCode' in data && typeof data.currencyCode === 'string' &&
        availableCurrencies.includes(data['currencyCode'] as ECurrencyCode)
      ) {
          return true;
      }
      notificationService.error(
        translate.instant('toastNotifications.headers.authError'),
        translate.instant('toastNotifications.messages.serverAuthError'),
      );

      return false;
    }

    const getValidatedMappedUserResponse = (data: unknown): ILoggedUser | null => {
      if (validateApiUserResponse(data)) {
        return ({
          currencyCode: data.currencyCode as ECurrencyCode,
          clientAccountId: data.clientAccountId,
          maxRiskScore: data.maxRiskScore,
          canReadEnglish: data.canReadEnglish,
          createdDate: data.createdDate,
          managementType: data.managementType,
          // todo: https://gitlab.com/dfcz/bwm-frontend/-/issues/50
          language: ELanguage['cs-CZ'], // not in api, CZ as default, user can probably change it in next version
          validContracts: data.validContracts,
          hasOpenOrder: data.hasOpenOrder,
          personalId: data.personalIdentificationNumber,
          name: data.firstName,
          surname: data.lastName,
          firstNameVocative: data.firstNameVocative,
          addresses: data.addresses,
          phoneNumber: data.phoneNumber,
          email: data.email,
          disclaimerShownAt: data.disclaimerShownAt,
          instrumentCategories: [...data.investmentQuestionnaire.instrumentCategories, EInstrumentCategory.CASH],
          appearanceMode: data.appearanceMode,
        });
      }
      return null;
    }

    const getContractsComposition = (contracts: IFrameworkContractOut[]): EContractsComposition => {
      if (contracts.length === 2) {
        return contracts.every(contract => !contract.dip)
          ? EContractsComposition.BOTH_NON_DIP
          : EContractsComposition.BOTH_MIXED;
      }

      if (contracts.length === 1) {
        return contracts[0].dip
          ? EContractsComposition.SINGLE_DIP
          : EContractsComposition.SINGLE_NON_DIP;
      }

      return EContractsComposition.NO_CONTRACT;
    }

    return {
      getCurrentUser,
      getInvestmentProfile,
      resetLoggedUser,
      resetState,
      setIsIdle,
      setLastActive,
    };
  }),

) {}
